/**
 * Garante que o contrato tem DistribuidorId preenchido.
 * Regra: se contrato.distribuidorId === 0 e contrato.clienteId > 0, usa o distribuidorId do cliente.
 * Se encontrar um distribuidorId válido, atualiza o contrato no backend via:
 *   PUT /Contrato/{id}/atualizar-distribuidor?distribuidorId=...&usuarioId=...
 *
 * Retorna um objeto { contrato, cliente, distribuidor, updated }
 * onde contrato pode vir já atualizado e cliente/distribuidor são os objetos carregados (se necessário).
 */

async function safeGet(api, url) {
  try {
    const resp = await api.get(url);
    return resp?.data ?? null;
  } catch {
    return null;
  }
}

export async function ensureContratoDistribuidorId({
  api,
  contrato,
  usuarioId,
  cacheClienteById,
  cacheDistribuidorById,
}) {
  if (!api) throw new Error("api obrigatório");
  if (!contrato?.id) return { contrato, cliente: null, distribuidor: null, updated: false };

  const contratoId = Number(contrato.id);
  const clienteId = Number(contrato.clienteId ?? 0);
  const distribuidorIdAtual = Number(contrato.distribuidorId ?? 0);

  let cliente = contrato.cliente ?? null;
  let distribuidor = contrato.distribuidor ?? null;

  if (!cliente && clienteId > 0) {
    if (cacheClienteById?.has(clienteId)) {
      cliente = cacheClienteById.get(clienteId);
    } else {
      cliente = await safeGet(api, `/Cliente/${clienteId}`);
      if (cacheClienteById && cliente) cacheClienteById.set(clienteId, cliente);
    }
  }

  if (distribuidorIdAtual > 0) {
    if (!distribuidor) {
      if (cacheDistribuidorById?.has(distribuidorIdAtual)) {
        distribuidor = cacheDistribuidorById.get(distribuidorIdAtual);
      } else {
        distribuidor = await safeGet(api, `/Distribuidor/${distribuidorIdAtual}`);
        if (cacheDistribuidorById && distribuidor) cacheDistribuidorById.set(distribuidorIdAtual, distribuidor);
      }
    }

    return {
      contrato: { ...contrato, cliente, distribuidor },
      cliente,
      distribuidor,
      updated: false,
    };
  }

  const distribuidorIdDoCliente = Number(cliente?.distribuidorId ?? 0);
  if (distribuidorIdDoCliente <= 0) {
    return { contrato: { ...contrato, cliente, distribuidor }, cliente, distribuidor, updated: false };
  }

  const uid = Number(usuarioId ?? 0);
  const url =
    `/Contrato/${contratoId}/atualizar-distribuidor?distribuidorId=${distribuidorIdDoCliente}` +
    (uid > 0 ? `&usuarioId=${uid}` : "");

  let contratoAtualizado = null;
  try {
    const resp = await api.put(url);
    contratoAtualizado = resp?.data ?? null;
  } catch {
    return { contrato: { ...contrato, cliente, distribuidor }, cliente, distribuidor, updated: false };
  }

  if (cacheDistribuidorById?.has(distribuidorIdDoCliente)) {
    distribuidor = cacheDistribuidorById.get(distribuidorIdDoCliente);
  } else {
    distribuidor = await safeGet(api, `/Distribuidor/${distribuidorIdDoCliente}`);
    if (cacheDistribuidorById && distribuidor) cacheDistribuidorById.set(distribuidorIdDoCliente, distribuidor);
  }

  return {
    contrato: { ...(contratoAtualizado ?? contrato), cliente, distribuidor },
    cliente,
    distribuidor,
    updated: true,
  };
}

